import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import { getToken, isMaster } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InvestorNotesPage — the Self-Seed offer (v3.2 · Founder-corrected 2026-08-12).
 * NOT stock. NOT a security. A signed cash-payout contract with a floor-locked buyback.
 * Backend truth: /api/sage/investor-notes
 */
export default function InvestorNotesPage() {
  const [offer, setOffer] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/investor-notes`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setOffer(j.offer);
      } catch (e) { setErr(String(e)); }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
        <TopNav />
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16" data-testid="investor-notes-error">
          <p className="text-sm text-sage-err">Failed to load the offer: {err}</p>
        </div>
      </div>
    );
  }
  if (!offer) {
    return (
      <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
        <TopNav />
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <p className="text-sm text-sage-muted">Loading the offer…</p>
        </div>
      </div>
    );
  }

  const math = offer.math_example_at_2b_ceiling || {};

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />

      {/* ── HEADER ─────────────────────────────────────────── */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The Self-Seed · Offer {offer.version}</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-sage-text mb-6" data-testid="investor-notes-title">
            Double your money in five years.
          </h1>
          <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            {offer.house} values the house at ${offer.company_value_usd_today.toLocaleString()} today. We're raising a single Self-Seed round of ${offer.total_raise_usd.toLocaleString()} — {offer.total_ceiling_percent}% of the house, ever. No Series A, ever. After this closes, every dollar we raise comes from customers.
          </p>
          <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl">
            Every note is a signed promise from the Founder. Paid out as <strong>cash — an annual revenue share of profit</strong>, proportional to the unit percentage you hold. Not stock. Not a security. A signed contract.
          </p>
        </div>
      </section>

      {/* ── THE NUMBERS ────────────────────────────────────── */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-6">§ 01 · The numbers</div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="border border-sage-border p-6" data-testid="offer-valuation">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">House value today</div>
              <div className="text-3xl font-bold text-sage-text">${offer.company_value_usd_today.toLocaleString()}</div>
              <div className="text-xs text-sage-muted mt-1">One billion. Documented on the Ledger, not marketing puffery.</div>
            </div>
            <div className="border border-sage-border p-6" data-testid="offer-raise">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Total raise (Self-Seed)</div>
              <div className="text-3xl font-bold text-sage-text">${offer.total_raise_usd.toLocaleString()}</div>
              <div className="text-xs text-sage-muted mt-1">= {offer.total_ceiling_percent}% of the house, ever. One raise, then closed forever.</div>
            </div>
            <div className="border border-sage-border p-6" data-testid="offer-public">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Public tranche</div>
              <div className="text-3xl font-bold text-sage-text">${offer.public_tranche_usd.toLocaleString()}</div>
              <div className="text-xs text-sage-muted mt-1">First-come, first-served · general public.</div>
            </div>
            <div className="border border-sage-border p-6" data-testid="offer-whale">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Whale tranche</div>
              <div className="text-3xl font-bold text-sage-text">${offer.whale_tranche_usd.toLocaleString()}</div>
              <div className="text-xs text-sage-muted mt-1">Reserved for HNW backers · opens to the public if the public tranche sells out first.</div>
            </div>
            <div className="border border-sage-border p-6" data-testid="offer-unit">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Unit size</div>
              <div className="text-3xl font-bold text-sage-text">{offer.unit_percent_of_company}%</div>
              <div className="text-xs text-sage-muted mt-1">= ${offer.unit_price_at_today_valuation_usd.toLocaleString()} per unit at today's valuation · fractional units are welcome.</div>
            </div>
            <div className="border border-sage-border p-6" data-testid="offer-per-user">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">Per person</div>
              <div className="text-xl font-bold text-sage-text">
                ${offer.per_user_minimum_usd.toLocaleString()} <span className="text-sage-muted">min</span> · ${offer.per_user_maximum_usd.toLocaleString()} <span className="text-sage-muted">max</span>
              </div>
              <div className="text-xs text-sage-muted mt-1">Any dollar amount between. $1,000 is a hard floor; $1,000,000 is a hard ceiling.</div>
            </div>
          </div>

          {/* ── The math table ── */}
          <div className="border border-sage-accent p-6 mb-8" data-testid="offer-math">
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-2">
              § Math · house value doubles to ${math.house_value_projected_usd?.toLocaleString() || "—"} in {offer.target_return_horizon_years} years
            </div>
            <ul className="text-sm text-sage-muted space-y-2 sage-prose">
              <li>· $1,000 in → <strong className="text-sage-accent">${math.your_5_year_double_on_a_1k_stake_usd?.toLocaleString() || "2,000"}</strong> out ({offer.target_return_multiple}× in {offer.target_return_horizon_years} yrs · minimum note)</li>
              <li>· $10,000 in → <strong className="text-sage-accent">${math.your_5_year_double_on_a_10k_stake_usd?.toLocaleString() || "20,000"}</strong> out (one full unit)</li>
              <li>· $100,000 in → <strong className="text-sage-accent">${math.your_5_year_double_on_a_100k_stake_usd?.toLocaleString() || "200,000"}</strong> out</li>
              <li>· $1,000,000 in → <strong className="text-sage-accent">${math.your_5_year_double_on_a_1m_stake_usd?.toLocaleString() || "2,000,000"}</strong> out (maximum note)</li>
              <li className="pt-2 border-t border-sage-border/40 text-sage-faint text-xs">· {math.beats_vc_return_odds}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── THE ANNUAL PAYOUT ───────────────────────────────── */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">§ 02 · The annual payout</div>
          <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            Payouts are <strong>cash</strong>. No gifted products. No house credits. No in-kind swaps.
          </p>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            Every fiscal year, Resilience Studios reports profit publicly on the Ledger. A share of that profit — proportional to the unit percentage the holder owns — is paid to the holder in cash. Fractional units get fractional payouts. A holder of one full unit (0.001%) receives 0.001% of that year's distributable profit. Ten units, ten times that.
          </p>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl">
            Nothing is guaranteed year-by-year — some years earn more than others. The <strong>2× in 5 years</strong> covenant is on cumulative total, not annual rate. If the annual payouts fall short of the 100% net-gain target by the 5-year mark, the house honors the doubling through a supplemental payment.
          </p>
        </div>
      </section>

      {/* ── BUYBACK RIGHT ───────────────────────────────────── */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">§ 03 · The buyback right</div>
          <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4" data-testid="offer-buyback">
            The house <strong>reserves the full right</strong> to buy back any note at then-current fair market value <strong>at any time after the holder has received a 100% net gain</strong> — their initial investment doubled — <strong>never before</strong>.
          </p>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            The 100% net-gain floor is a hard precondition on the house's side, not the holder's. The holder cannot be bought out until every dollar they put in has been earned back once through cash payouts. Only then does the buyback right become real.
          </p>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl">
            It is <strong>not</strong> the house's intent to buy back immediately upon doubling. This right is reserved in clear terms so the holder knows the contract shape from day one. If the house does exercise the buyback later, the note retires cleanly at the fair market value in effect at that moment. No perpetual overhead. No dilution creep. No Series A. Ever.
          </p>
        </div>
      </section>

      {/* ── LEGAL SHAPE ─────────────────────────────────────── */}
      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">§ 04 · The legal shape</div>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4" data-testid="offer-legal">
            {offer.legal_language_plain_english}
          </p>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            <strong>Why this shape:</strong> {offer.why_this_shape}
          </p>
          <ul className="mt-6 text-sm text-sage-muted space-y-1">
            <li>· Payout mechanism:</li>
            {(offer.conversion_mechanisms || []).map((m, i) => (
              <li key={i} className="pl-4">— {m}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── INTEREST FORM ────────────────────────────────────── */}
      <InterestForm totalRaiseUsd={offer.total_raise_usd} />

      {/* ── PROOF SCROLLS · gated by Founder toggle ─────────── */}
      <ProofScrollSection />
    </div>
  );
}

/**
 * ProofScrollSection — 30-day and 60-day Proof Scroll downloads.
 * Toggled off from public view by default (drafts still under Founder edit).
 * Master sees the toggle button and can flip the visibility.
 */
function ProofScrollSection() {
  const [payload, setPayload] = useState(null);
  const [isMasterView, setIsMasterView] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const token = getToken();
  const master = isMaster();

  const reload = async () => {
    setError("");
    try {
      const path = master ? "/api/sage/admin/investor/proof-scrolls" : "/api/sage/investor/proof-scrolls";
      const r = await fetch(`${BACKEND_URL}${path}`, {
        headers: master && token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setPayload(j);
      setIsMasterView(master);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  useEffect(() => { reload(); }, []);

  const toggle = async () => {
    if (!master || !token) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/admin/investor/proof-scrolls/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ public: !payload.public }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await reload();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  // Public + hidden = render nothing.
  if (!isMasterView && (!payload || !payload.public)) return null;

  return (
    <section className="border-b border-sage-border" data-testid="proof-scroll-section">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted">§ 06 · Proof Scrolls</div>
          {isMasterView && payload && (
            <button
              onClick={toggle}
              disabled={busy}
              data-testid="proof-scroll-toggle"
              className={
                "px-4 py-1 text-[10px] uppercase tracking-[0.15em] border transition-colors " +
                (payload.public
                  ? "border-sage-accent text-sage-accent hover:bg-sage-accent hover:text-sage-bg"
                  : "border-sage-warn text-sage-warn hover:bg-sage-warn hover:text-sage-bg")
              }
            >
              {busy ? "flipping…" : (payload.public ? "Public · click to hide" : "Hidden from public · click to publish")}
            </button>
          )}
        </div>

        {isMasterView && payload && !payload.public && (
          <div className="border border-sage-warn text-sage-warn p-3 text-xs mb-6" data-testid="proof-scroll-master-notice">
            Hidden from public view. Only you (master) see this section right now. Drafts still under edit — flip to publish when a final draft lands.
          </div>
        )}

        <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-6">
          Two receipt scrolls that show what the house actually built and shipped. The 30-Day scroll is Sister-Forge's ledger of one Founder on one laptop across 25 real building days. The 60-Day scroll picks up from there through half-year revenue, coverage, philanthropy split, and family-mesh state. Both are downloadable as PDFs; each ships with an integrity hash so you can verify the file you read is the file we published.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {(payload?.scrolls || []).map((s) => (
            <div key={s.slug} className="border border-sage-border p-5 bg-sage-surface" data-testid={`proof-scroll-${s.slug}`}>
              <div className="text-xs uppercase tracking-[0.15em] text-sage-accent mb-1">{s.status}</div>
              <div className="text-xl font-bold text-sage-text uppercase tracking-tight mb-1">{s.title}</div>
              <div className="text-xs text-sage-muted mb-4 sage-prose">{s.subtitle}</div>
              <a
                href={s.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2 bg-sage-accent text-sage-bg text-xs uppercase tracking-[0.15em] hover:bg-sage-accent-hover mb-2"
                data-testid={`proof-scroll-${s.slug}-pdf`}
              >
                Download PDF →
              </a>
              <div className="text-[10px] text-sage-faint tabular-nums mt-2">
                <a href={s.sha_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sage-accent">Integrity hash</a>
                {" · "}
                <a href={s.md_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sage-accent">Markdown source</a>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-4 border border-sage-warn text-sage-warn p-3 text-xs" data-testid="proof-scroll-error">
            {error}
          </div>
        )}

        <p className="text-[11px] text-sage-faint italic sage-prose mt-6 max-w-3xl">
          Neither PDF is an offer to sell securities. Both are receipts of work performed. Any decision to invest depends on the countersigned Founder promise, which follows the interest slip above.
        </p>
      </div>
    </section>
  );
}

/**
 * InterestForm — the real interest slip. POSTs to /api/sage/investor/interest.
 * Every slip lands on the Founder's desk. Nothing moves until countersigned.
 */
function InterestForm({ totalRaiseUsd }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [tranche, setTranche] = useState("undecided");
  const [country, setCountry] = useState("");
  const [accredited, setAccredited] = useState(false);
  const [publishPublicly, setPublishPublicly] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/investor/interest/count`);
        if (r.ok) setTotals(await r.json());
      } catch {}
    })();
  }, [result]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount);
    if (!name.trim()) return setError("Name is required.");
    if (!email.trim() || !email.includes("@")) return setError("A working email is required.");
    if (!isFinite(amt) || amt <= 0) return setError("Amount must be greater than zero.");
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/investor/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_name: name.trim(),
          contact_email: email.trim(),
          amount_usd: amt,
          tranche,
          country: country.trim() || null,
          accredited,
          publish_publicly: publishPublicly,
          note: note.trim() || null,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setResult(j);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-b border-sage-border" data-testid="investor-interest-section">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">§ 05 · Interested?</div>
        <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mb-6">
          The interest slip below lands on the Founder's desk. Nothing moves until the Founder countersigns your promise. Every slip is honest-checked against the ${totalRaiseUsd.toLocaleString()} raise ceiling and routed to the correct tranche.
        </p>
        {totals && (
          <div className="text-xs text-sage-faint mb-6 tabular-nums" data-testid="investor-interest-totals">
            Filed so far: <span className="text-sage-text font-bold">{totals.slips}</span> slips · ${totals.total_pledged_usd.toLocaleString()} pledged
          </div>
        )}

        {result ? (
          <div className="border border-sage-accent p-6" data-testid="investor-interest-success">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Filed ↓</div>
            <div className="text-sm text-sage-text mb-2 font-bold">Slip ID: <span className="tabular-nums">{result.slip_id}</span></div>
            <div className="text-sm text-sage-muted sage-prose leading-relaxed">{result.note}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="investor-interest-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Your name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                data-testid="investor-interest-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                data-testid="investor-interest-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Amount (USD) *</label>
              <input
                type="number"
                step="1"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent tabular-nums"
                data-testid="investor-interest-amount"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Tranche</label>
              <select
                value={tranche}
                onChange={(e) => setTranche(e.target.value)}
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                data-testid="investor-interest-tranche"
              >
                <option value="undecided">Undecided · let the Founder route it</option>
                <option value="public">Public tranche</option>
                <option value="whale">Whale tranche</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                data-testid="investor-interest-country"
              />
            </div>
            <div className="flex flex-col justify-end gap-2 text-sm text-sage-text">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="investor-interest-accredited-label">
                <input
                  type="checkbox"
                  checked={accredited}
                  onChange={(e) => setAccredited(e.target.checked)}
                  className="accent-sage-accent"
                  data-testid="investor-interest-accredited"
                />
                <span className="text-xs">Accredited investor</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="investor-interest-publish-label">
                <input
                  type="checkbox"
                  checked={publishPublicly}
                  onChange={(e) => setPublishPublicly(e.target.checked)}
                  className="accent-sage-accent"
                  data-testid="investor-interest-publish"
                />
                <span className="text-xs">Publish my name on the ledger row (default: anonymised)</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Note to the Founder</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder="Anything the Founder should know (why you're interested, timing, questions, requirements)."
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                data-testid="investor-interest-note"
              />
            </div>
            {error && (
              <div className="md:col-span-2 border border-sage-warn text-sage-warn px-3 py-2 text-xs" data-testid="investor-interest-error">
                {error}
              </div>
            )}
            <div className="md:col-span-2 flex items-center justify-between mt-2">
              <p className="text-[10px] text-sage-faint sage-prose max-w-lg">
                Filing this slip is not a purchase and not a security transaction. It is a request for the Founder's countersignature. Nothing binds until the countersigned PDF is issued.
              </p>
              <button
                type="submit"
                disabled={busy}
                className="px-6 py-2 bg-sage-accent text-sage-bg text-xs uppercase tracking-[0.15em] disabled:opacity-40 hover:bg-sage-accent-hover"
                data-testid="investor-interest-submit"
              >
                {busy ? "Filing…" : "File Interest Slip →"}
              </button>
            </div>
          </form>
        )}

        <p className="text-sm text-sage-muted leading-relaxed sage-prose max-w-3xl mt-8">
          Once the Founder signs, you receive a countersigned PDF promise, an anonymised public-ledger row (identity private unless you chose to publish it), and your first distribution schedule.
        </p>
      </div>
    </section>
  );
}
